import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  SpeakerWaveIcon,
  ClockIcon,
  UserGroupIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { queueService, QueueDisplay as QueueDisplayType } from '../../services/queueService';
import { clinicSettingsService } from '../../services/clinicSettings';
import { ClinicSettings } from '../../types/userManagement';
import { getImageUrl } from '../../utils/imageUtils';

const QueueDisplay: React.FC = () => {
  const navigate = useNavigate();
  const { serviceType } = useParams<{ serviceType?: string }>();
  const [searchParams] = useSearchParams();
  const [displays, setDisplays] = useState<QueueDisplayType[]>([]);
  const [clinicInfo, setClinicInfo] = useState<Partial<ClinicSettings> | null>(null);
  
  // Ensure displays is always an array
  const safeDisplays = React.useMemo(() => {
    return Array.isArray(displays) ? displays : [];
  }, [displays]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [error, setError] = useState<string | null>(null);

  const displayName = searchParams.get('name') || (serviceType ? `Poli ${serviceType.toUpperCase()}` : 'Display Antrian');
  const autoRefresh = searchParams.get('refresh') !== 'false';
  const refreshInterval = parseInt(searchParams.get('interval') || '5000'); // 5 seconds default

  // Load clinic settings for logo and clinic name
  useEffect(() => {
    const fetchClinicSettings = async () => {
      try {
        const data = await clinicSettingsService.getPublicSettings();
        if (data) {
          setClinicInfo(data);
        }
      } catch (err) {
        // Fallback silently if settings fail
      }
    };
    fetchClinicSettings();
  }, []);

  const loadDisplayData = useCallback(async () => {
    try {
      setError(null);
      const response = await queueService.getQueueDisplay(serviceType);
      
      // Ensure we always set an array
      if (response && response.data && Array.isArray(response.data)) {
        setDisplays(response.data);
      } else {
        setDisplays([]);
        setError('Format data tidak valid');
      }
    } catch (err: any) {
      setDisplays([]); // Ensure displays is always an array
      setError('Gagal memuat data antrian');
    } finally {
      setLoading(false);
    }
  }, [serviceType]);

  // Update current time
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timeInterval);
  }, []);

  // Load initial data
  useEffect(() => {
    loadDisplayData();
  }, [loadDisplayData]);

  // Auto refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadDisplayData();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, loadDisplayData]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('id-ID', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getServiceTypeDisplay = (type: string) => {
    const serviceMap = {
      doctor: 'DOKTER',
      nurse: 'PERAWAT',
      lab: 'LABORATORIUM',
      pharmacy: 'APOTEK',
      radiology: 'RADIOLOGI',
      dental: 'GIGI',
      midwife: 'BIDAN'
    };
    
    return serviceMap[type as keyof typeof serviceMap] || type.toUpperCase();
  };

  const clinicName = clinicInfo?.clinic_name || 'KLINIK CONEC';
  const logoUrl = getImageUrl(clinicInfo?.logo_url || clinicInfo?.clinic_logo);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-700 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-xl">Memuat data antrian...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 to-red-700 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-3xl font-bold mb-2">Error</h1>
          <p className="text-xl">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-700 text-white">
      {/* Header */}
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border-b border-white border-opacity-20 sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Back Button, Logo, and Clinic Name */}
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => {
                  if (window.history.length > 1) {
                    navigate(-1);
                  } else {
                    navigate('/dashboard');
                  }
                }}
                className="inline-flex items-center space-x-2 px-3 py-2 rounded-xl bg-white bg-opacity-15 hover:bg-opacity-25 active:scale-95 transition-all text-white border border-white border-opacity-20 shadow-sm group"
                title="Kembali"
              >
                <ArrowLeftIcon className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
                <span className="text-sm font-semibold pr-1">Kembali</span>
              </button>

              {logoUrl ? (
                <div className="h-12 w-12 rounded-xl bg-white p-1 shadow-md flex items-center justify-center overflow-hidden">
                  <img
                    src={logoUrl}
                    alt={clinicName}
                    className="max-h-full max-w-full object-contain"
                    onError={(e) => {
                      (e.target as HTMLElement).style.display = 'none';
                    }}
                  />
                </div>
              ) : (
                <div className="h-12 w-12 rounded-xl bg-white bg-opacity-20 flex items-center justify-center text-2xl shadow-md border border-white border-opacity-30">
                  🏥
                </div>
              )}

              <div>
                <h1 className="text-2xl font-bold tracking-tight">{clinicName}</h1>
                <p className="text-blue-100 text-sm font-medium">{displayName}</p>
              </div>
            </div>
            
            {/* Right: Clock & Date */}
            <div className="text-right">
              <div className="text-3xl font-bold font-mono">
                {formatTime(currentTime)}
              </div>
              <div className="text-blue-100 text-sm">
                {formatDate(currentTime)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {!Array.isArray(safeDisplays) || safeDisplays.length === 0 ? (
          <div className="text-center py-20 bg-white bg-opacity-5 backdrop-blur-sm rounded-3xl border border-white border-opacity-10 max-w-2xl mx-auto shadow-xl">
            <UserGroupIcon className="mx-auto h-24 w-24 text-white text-opacity-40 mb-6" />
            <h2 className="text-3xl font-bold mb-3">Tidak Ada Antrian</h2>
            <p className="text-lg text-blue-100 max-w-md mx-auto">
              Belum ada data antrian pasien untuk ditampilkan saat ini.
            </p>
            <p className="text-sm text-blue-200 text-opacity-70 mt-2">
              Layar ini akan otomatis terupdate ketika ada antrian baru yang dipanggil.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {Array.isArray(safeDisplays) && safeDisplays.map((display) => (
              <div key={display.id} className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8 border border-white border-opacity-20">
                {/* Service Header */}
                <div className="text-center mb-8">
                  <h2 className="text-4xl font-bold mb-2">
                    ANTRIAN {getServiceTypeDisplay(display.service_type)}
                  </h2>
                  <div className="w-24 h-1 bg-white mx-auto rounded-full"></div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Current Queue */}
                  <div className="text-center">
                    <div className="bg-green-500 bg-opacity-20 rounded-2xl p-8 border border-green-400 border-opacity-30">
                      <div className="flex items-center justify-center mb-4">
                        <SpeakerWaveIcon className="h-8 w-8 mr-3" />
                        <h3 className="text-2xl font-semibold">SEDANG DIPANGGIL</h3>
                      </div>
                      
                      {display.current_queue ? (
                        <div>
                          <div className="text-8xl font-bold mb-4 font-mono">
                            {display.current_queue.queue_number}
                          </div>
                          <div className="text-2xl mb-2">
                            {display.current_queue.patient_name}
                          </div>
                          <div className="text-lg text-green-100">
                            Dipanggil pukul {display.current_queue.called_at}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="text-6xl mb-4">-</div>
                          <div className="text-xl text-green-100">
                            Belum ada yang dipanggil
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Waiting Queue */}
                  <div>
                    <div className="bg-blue-500 bg-opacity-20 rounded-2xl p-8 border border-blue-400 border-opacity-30 h-full">
                      <div className="flex items-center justify-center mb-6">
                        <ClockIcon className="h-8 w-8 mr-3" />
                        <h3 className="text-2xl font-semibold">ANTRIAN MENUNGGU</h3>
                      </div>
                      
                      {display.waiting_queues && display.waiting_queues.length > 0 ? (
                        <div className="space-y-4">
                          {display.waiting_queues.slice(0, 5).map((queue, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between bg-white bg-opacity-10 rounded-lg p-4"
                            >
                              <div className="flex items-center">
                                <div className="text-3xl font-bold font-mono mr-4 w-16">
                                  {queue.queue_number}
                                </div>
                                <div>
                                  <div className="text-lg font-medium">
                                    {queue.patient_name}
                                  </div>
                                  {queue.priority > 1 && (
                                    <div className="text-sm text-yellow-300">
                                      {queue.priority === 2 ? '⭐ Prioritas' : '🚨 Darurat'}
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="text-2xl font-bold text-blue-100">
                                #{index + 1}
                              </div>
                            </div>
                          ))}
                          
                          {display.waiting_queues.length > 5 && (
                            <div className="text-center text-blue-100 text-lg">
                              ... dan {display.waiting_queues.length - 5} antrian lainnya
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center">
                          <div className="text-4xl mb-4">📋</div>
                          <div className="text-xl text-blue-100">
                            Tidak ada antrian menunggu
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer Info */}
                <div className="mt-8 text-center">
                  <div className="flex items-center justify-center space-x-8 text-sm text-blue-100">
                    <div>
                      Update terakhir: {display.last_updated ? new Date(display.last_updated).toLocaleTimeString('id-ID') : '-'}
                    </div>
                    <div>•</div>
                    <div>
                      Total antrian: {(display.current_queue ? 1 : 0) + (display.waiting_queues?.length || 0)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Auto Refresh Indicator */}
      {autoRefresh && (
        <div className="fixed bottom-4 right-4">
          <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-4 py-2 flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm">Auto refresh</span>
          </div>
        </div>
      )}
      
      {/* Instructions overlay for setup */}
      {searchParams.get('setup') === 'true' && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white text-gray-900 rounded-lg p-8 max-w-2xl mx-4">
            <h3 className="text-2xl font-bold mb-4">Setup Display Antrian</h3>
            <div className="space-y-4 text-sm">
              <p><strong>URL Parameters yang tersedia:</strong></p>
              <ul className="list-disc list-inside space-y-1">
                <li><code>?name=Nama Display</code> - Nama untuk display ini</li>
                <li><code>?refresh=false</code> - Matikan auto refresh</li>
                <li><code>?interval=10000</code> - Set interval refresh (ms)</li>
                <li><code>?setup=true</code> - Tampilkan panduan ini</li>
              </ul>
              <p><strong>Contoh URL:</strong></p>
              <code className="block bg-gray-100 p-2 rounded">
                /queue-display/doctor?name=Ruang Dokter 1&interval=3000
              </code>
            </div>
            <button
              onClick={() => window.location.search = window.location.search.replace('setup=true', '').replace('&setup=true', '').replace('?setup=true&', '?')}
              className="mt-6 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Tutup
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueueDisplay;